import React, { useState } from 'react';
import { authAPI } from '../services/api';

const MFASetupPopup = ({ onClose }) => {
  const [step, setStep] = useState('intro');
  const [qrCode, setQrCode] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSetupMFA = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await authAPI.setupMFA();
      setQrCode(response.qrcode);
      setSetupToken(response.setup_token);
      setStep('setup');
    } catch (err) {
      setError(err.message || 'Failed to setup MFA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await authAPI.completeMFASetup({
        setup_token: setupToken,
        mfa_code: verificationCode
      });
      setStep('success');
    } catch (err) {
      setError(err.message || 'Failed to verify MFA code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden transform transition-all">
        {/* Header */}
        <div className="relative">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-8">
          {step === 'intro' && (
            <div className="text-center">
              <div className="mb-6">
                <svg className="mx-auto h-24 w-24 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Enhance Your Account Security
              </h2>
              <p className="text-gray-600 mb-8">
                Set up Two-Factor Authentication to add an extra layer of security to your account.
                Use any authenticator app like Google Authenticator or Authy.
              </p>
              <div className="space-x-4">
                <button
                  onClick={handleSetupMFA}
                  disabled={loading}
                  className="bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
                >
                  {loading ? 'Setting up...' : 'Set Up MFA'}
                </button>
                <button
                  onClick={onClose}
                  className="text-gray-600 px-6 py-3 rounded-lg font-medium hover:text-gray-800"
                >
                  Maybe Later
                </button>
              </div>
              <label className="flex items-center justify-center gap-2 mt-6 cursor-pointer">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    if (e.target.checked) {
                      localStorage.setItem('mfa_popup_dismissed', 'true');
                      onClose();
                    }
                  }}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-500">Don't show this again</span>
              </label>
            </div>
          )}

          {step === 'setup' && (
            <div className="text-center">
              <div className="mb-6">
                <svg className="mx-auto h-16 w-16 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v1m6 11h2m-6 0h-2m0 0H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-4">Scan QR Code</h3>
              <div className="bg-gray-50 p-4 rounded-lg mb-6 flex justify-center">
                <img src={`data:image/png;base64,${qrCode}`} alt="MFA QR Code" className="max-w-[200px]" />
              </div>
              <p className="text-gray-600 mb-6">
                Scan this QR code with your authenticator app, then enter the 6-digit code below
              </p>
              <input
                type="text"
                maxLength="6"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                placeholder="Enter 6-digit code"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 mb-4 text-center text-2xl tracking-widest"
              />
              {error && (
                <p className="text-red-600 mb-4">{error}</p>
              )}
              <div className="space-x-4">
                <button
                  onClick={handleVerifySetup}
                  disabled={loading}
                  className="bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
                >
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>
                <button
                  onClick={onClose}
                  className="text-gray-600 px-6 py-3 rounded-lg font-medium hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center">
              <div className="mb-6">
                <svg className="mx-auto h-24 w-24 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                MFA Setup Complete!
              </h3>
              <p className="text-gray-600 mb-8">
                Your account is now protected with Two-Factor Authentication.
              </p>
              <button
                onClick={onClose}
                className="bg-green-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MFASetupPopup;
